import { useState, useMemo, useCallback } from "react";
import { useSources } from "../hooks/useSources";
import { useItems } from "../hooks/useItems";
import { ArticleDrawer } from "./ArticleDrawer";
import { TagBadge } from "./TagBadge";
import type { ItemListEntry } from "../types";

function cleanAuthor(author: string | null): string {
  if (!author) return "";
  const match = author.match(/"?([^"<]+)"?\s*<?/);
  return match?.[1]?.trim() || author;
}

export function RssFeedReaderPage() {
  const { sources } = useSources();
  const rssFeeds = useMemo(() => sources.filter((s) => s.type === "rss_feed" && s.enabled), [sources]);

  const [activeSourceId, setActiveSourceId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const filters = useMemo(() => ({
    page: 1,
    page_size: 50,
    ...(activeSourceId ? { source_id: activeSourceId } : {}),
    ...(searchQuery ? { q: searchQuery } : {}),
  }), [activeSourceId, searchQuery]);

  const { data, loading, reload } = useItems(filters);
  const items = data?.items ?? [];

  // Gruppiere nach Datum fuer Timeline
  const groupedByDate = useMemo(() => {
    const groups = new Map<string, ItemListEntry[]>();
    for (const item of items) {
      const dateStr = item.published_at
        ? new Date(item.published_at).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
        : "Unbekanntes Datum";
      const list = groups.get(dateStr) ?? [];
      list.push(item);
      groups.set(dateStr, list);
    }
    return Array.from(groups.entries());
  }, [items]);

  const handleCloseDetail = useCallback(() => {
    setSelectedId(null);
    reload();
  }, [reload]);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* Filter-Leiste: Dropdown + Suche */}
      <div style={{
        display: "flex", gap: "0.75rem", marginBottom: "1.25rem",
        alignItems: "center",
      }}>
        <select
          value={activeSourceId ?? ""}
          onChange={(e) => setActiveSourceId(e.target.value ? Number(e.target.value) : null)}
          style={{
            height: "2.25rem", padding: "0 2rem 0 0.625rem",
            border: "1px solid var(--color-border, #e2e8f0)",
            borderRadius: "var(--radius-sm, 6px)",
            fontSize: "0.8125rem", fontWeight: 500,
            background: "var(--color-bg-card, white)",
            color: "var(--color-text, #0f172a)",
            cursor: "pointer", outline: "none",
            appearance: "none", WebkitAppearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.5rem center",
            flexShrink: 0, width: 220,
          }}
        >
          <option value="">Alle Feeds ({data?.total ?? 0})</option>
          {rssFeeds.map((feed) => (
            <option key={feed.id} value={feed.id}>{feed.name}</option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Artikel durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1, height: "2.25rem", padding: "0 0.75rem",
            fontSize: "0.8125rem",
            border: "1px solid var(--color-border, #e2e8f0)",
            borderRadius: "var(--radius-sm, 6px)",
            background: "var(--color-bg-card, white)",
            color: "var(--color-text, #0f172a)",
            outline: "none",
          }}
        />
      </div>

      {/* Timeline */}
      {loading ? (
        <p style={{ textAlign: "center", color: "var(--color-text-muted)", padding: "2rem" }}>Laden...</p>
      ) : items.length === 0 ? (
        <p style={{ textAlign: "center", color: "var(--color-text-muted)", padding: "2rem" }}>Keine Artikel gefunden</p>
      ) : (
        <div>
          {groupedByDate.map(([dateLabel, dateItems]) => (
            <div key={dateLabel} style={{ marginBottom: "1.5rem" }}>
              {/* Datum-Header */}
              <div style={{
                display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem",
              }}>
                <span style={{
                  fontSize: "0.8125rem", fontWeight: 600,
                  color: "var(--color-primary, #2563eb)", whiteSpace: "nowrap",
                }}>
                  {dateLabel}
                </span>
                <div style={{ flex: 1, height: 1, background: "var(--color-border, #e2e8f0)" }} />
                <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  {dateItems.length} Artikel
                </span>
              </div>

              {/* Artikel-Liste */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {dateItems.map((item) => (
                  <article
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    style={{
                      display: "flex", gap: "1rem", padding: "0.75rem 1rem",
                      background: "var(--color-bg-card, white)",
                      border: "1px solid var(--color-border, #e2e8f0)",
                      borderRadius: "var(--radius-md, 8px)",
                      cursor: "pointer", transition: "all 0.15s",
                      borderLeft: item.is_read ? "3px solid transparent" : "3px solid var(--color-primary, #2563eb)",
                      opacity: item.is_read ? 0.7 : 1,
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-md, 0 4px 12px rgba(0,0,0,0.07))"; }}
                    onMouseOut={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                  >
                    {/* Zeitstempel */}
                    <div style={{
                      flexShrink: 0, width: 70, textAlign: "right",
                      fontSize: "0.75rem", color: "var(--color-text-muted, #94a3b8)",
                      paddingTop: "0.125rem",
                    }}>
                      {item.published_at ? new Date(item.published_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : ""}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: "0.9375rem",
                        fontWeight: item.is_read ? 400 : 600,
                        color: "var(--color-text, #0f172a)",
                        marginBottom: "0.25rem",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {item.title || "Ohne Titel"}
                      </div>
                      <div style={{
                        display: "flex", alignItems: "center", gap: "0.5rem",
                        fontSize: "0.75rem", color: "var(--color-text-muted, #94a3b8)",
                      }}>
                        {item.source_category && (
                          <span style={{
                            fontSize: "0.625rem", fontWeight: 600,
                            padding: "0.0625rem 0.375rem", borderRadius: "999px",
                            background: item.source_category === "official_authority" ? "#dbeafe" : "#f3f4f6",
                            color: item.source_category === "official_authority" ? "#1e40af" : "#6b7280",
                            textTransform: "uppercase",
                          }}>
                            {item.source_category === "official_authority" ? "Behörde" : item.source_category === "news_outlet" ? "News" : item.source_category}
                          </span>
                        )}
                        <span>{cleanAuthor(item.author)}</span>
                        {item.tags.length > 0 && (
                          <div style={{ display: "flex", gap: "0.25rem" }}>
                            {item.tags.slice(0, 3).map((t) => <TagBadge key={t} tag={t} small />)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Score */}
                    {item.relevance_score !== null && (
                      <div style={{
                        flexShrink: 0, display: "flex", alignItems: "center",
                        fontSize: "0.75rem", fontWeight: 700,
                        color: item.relevance_score >= 70 ? "var(--color-success, #16a34a)" : "var(--color-text-muted, #94a3b8)",
                      }}>
                        {item.relevance_score}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Artikel-Detail Drawer */}
      {selectedId !== null && (
        <ArticleDrawer
          itemId={selectedId}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
}
